import clsx from 'clsx'
import type { FunctionComponent } from 'react'
import { filterNonEmpty } from '../../../utils/filterNonEmpty'
import { InternalLink } from '../../const/Links'
import { BAR_VISIBLE_ITEMS } from '../../const/digitContants'
import { Translation, useTranslate } from '../../contexts/TranslationContext'
import type { ProductViewResult } from '../../data/ProductFragment'
import { ProductImage } from '../product/ProductImage'
import { Button } from '../shared/Button'
import { CustomTooltip } from '../shared/CustomTooltip'
import { Hidden } from '../shared/Hidden'
import { LoadingComparator } from '../shared/LoadingComparator'
import { SimpleSwiper } from '../swiper/SimpleSwiper'
import style from './ComparatorBar.module.sass'

type ComparatorBarProps = {
	comparedItems: ((ProductViewResult & { bestIndex?: number | boolean }) | undefined)[]
	removeAll: () => void
	removeItem: (id: string) => void
	isLoading: boolean
}

export const ComparatorBar: FunctionComponent<ComparatorBarProps> = ({
	comparedItems,
	removeAll,
	removeItem,
	isLoading = false,
}) => {
	const translate = useTranslate()

	const items = comparedItems.filter(filterNonEmpty)

	return (
		<div className={style.bar}>
			<div className={style.wrapper}>
				<Hidden breakpoint="desktopDown">
					<div className={style.title}>
						<Translation tr="comparatorBar.title" />
					</div>
				</Hidden>
				{isLoading ? (
					<LoadingComparator />
				) : (
					<>
						<Hidden breakpoint="desktopDown">
							{items && items.length > BAR_VISIBLE_ITEMS ? (
								<div className={style.itemWrapper}>
									{items
										.map(
											(item) =>
												item && (
													<div key={item.id} data-tooltip-id={`tooltipProductImage_${item.slug}`} data-tip>
														<ProductImage
															title={item.name}
															url={`${InternalLink.Product}${item.slug}`}
															imageUrl={item.heurekaImage?.url ?? item.image?.url ?? '/images/placeholder-product.png'}
															handleClick={removeItem}
															id={item.id}
														/>
														<CustomTooltip
															tooltipId={`tooltipProductImage_${item.slug}`}
															textAfterHover={
																<span className={style.tooltipName}>
																	{item.productLine
																		? `${item.brand?.name} ${item.productLine.name} ${item.name}`
																		: `${item.brand?.name} ${item.name}`}
																</span>
															}
														/>
													</div>
												),
										)
										.slice(0, BAR_VISIBLE_ITEMS)}
									<div className={style.additional}>+ {items.length - BAR_VISIBLE_ITEMS}</div>
								</div>
							) : (
								<div className={clsx(style.itemWrapper, style.itemWrapperSmall)}>
									{items.map(
										(item) =>
											item && (
												<div key={item.id} data-tooltip-id={`tooltipProductImageSmall_${item.slug}`} data-tip>
													<ProductImage
														title={item.name}
														url={`${InternalLink.Product}${item.slug}`}
														imageUrl={item.heurekaImage?.url ?? item.image?.url ?? '/images/placeholder-product.png'}
														handleClick={removeItem}
														id={item.id}
													/>
													<CustomTooltip
														tooltipId={`tooltipProductImageSmall_${item.slug}`}
														textAfterHover={
															<span className={style.tooltipName}>
																{item.productLine
																	? `${item.brand?.name} ${item.productLine.name} ${item.name}`
																	: `${item.brand?.name} ${item.name}`}
															</span>
														}
													/>
												</div>
											),
									)}
								</div>
							)}
						</Hidden>
						<Hidden breakpoint="desktopUp">
							<div className={style.swiperWrapper}>
								<SimpleSwiper>
									{items.map(
										(item) =>
											item && (
												<div key={item.id} data-tooltip-id={`tooltipProductImageSmall_${item.slug}`} data-tip>
													<ProductImage
														key={item.id}
														title={item.name}
														url={`${InternalLink.Product}${item.slug}`}
														imageUrl={item.heurekaImage?.url ?? item.image?.url ?? '/images/placeholder-product.png'}
														handleClick={removeItem}
														id={item.id}
													/>
													<CustomTooltip
														tooltipId={`tooltipProductImageSmall_${item.slug}`}
														textAfterHover={
															<span className={style.tooltipName}>
																{item.productLine
																	? `${item.brand?.name} ${item.productLine.name} ${item.name}`
																	: `${item.brand?.name} ${item.name}`}
															</span>
														}
													/>
												</div>
											),
									)}
								</SimpleSwiper>
							</div>
						</Hidden>
					</>
				)}
				<div className={style.buttonGroup}>
					<div className={style.buttonWrapper}>
						<Button
							type={{ variant: 'bordered' }}
							label={translate('comparatorBar.button.deleteAll')}
							onClick={removeAll}
						/>
					</div>
					<div className={style.buttonWrapper}>
						<Button
							type={{ fullWidth: true }}
							label={
								isLoading ? (
									<span className={style.buttonWithLoader}>
										<Translation tr="comparatorBar.button.compare" />
										<span className={style.loader} />
									</span>
								) : (
									`${translate('comparatorBar.button.compare')} (${items?.length})`
								)
							}
							url={InternalLink.Comparator}
						/>
					</div>
				</div>
			</div>
		</div>
	)
}
